import { mergeClasses, Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { FC } from 'react';
import { useAccountAmountStyles } from '../../../hooks/useAccountAmountStyles';
import { useDrillDownStyles } from '../../../hooks/useDrillDownStyles';
import { formatNumberForDisplay, parseNumericValue } from '../../../utils/NumberUtils';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import { JournalEntryDialogButton } from '../JournalEntryDialogButton';
import { AccountAmountWhitePaperRow } from '../ReportDrillDownUtils';
import { AccountAmountBookEntryRow } from './AccountAmountBookEntryRow';
import { AdjustmentTypeSpacerCell } from './AdjustmentTypeSpacerCell';
import { useTreeLineStyles } from './TreeLineUtils';
import { IAccountAmountBookInfoTreeProps } from './types';

function stopClickPropagation(e: React.MouseEvent): void {
    e.stopPropagation();
}

function reformatNumericText(value: string, numberFormatter: (v: number) => string): string {
    return numberFormatter(parseNumericValue(value, true));
}

export const AccountAmountBookInfoTree: FC<IAccountAmountBookInfoTreeProps> = (props: IAccountAmountBookInfoTreeProps) => {
    const styles = useAccountAmountStyles();
    const drillDownStyles = useDrillDownStyles();
    const treeLineStyles = useTreeLineStyles();
    const row: AccountAmountWhitePaperRow = props.row;

    const shouldShowJournalEntry: boolean = props.journalEntryButtonDisplayState !== 'invisible' && !row.bookEntries?.length;
    return (
        <Tree
            appearance='transparent'
            navigationMode='treegrid'
            key={row.uiKey}
            className={mergeClasses(styles.treeNoGap)}
            onClick={stopClickPropagation}
            collapseMotion={null}
        >
            <TreeItem itemType='leaf' key={row.uiKey} value={row.uiKey!}>
                <TreeItemLayout
                    main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain, props.containerClassName) }}
                    className={mergeClasses('book-summary-row', styles.alignItemsStart, styles.summaryRow, props.className)}
                >
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            styles.alignContentCenter,
                            styles.fontWeightSemibold,
                            styles.accountColumn,
                            styles.fontSize
                        )}
                        data-automation-key='account'
                    >
                        {row.account}
                    </div>
                    <AdjustmentTypeSpacerCell />
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            shouldShowJournalEntry ? styles.descriptionColumnSmall : styles.descriptionColumn,
                            styles.alignContentCenter,
                            styles.fontWeightSemibold,
                            styles.fontSize
                        )}
                        data-automation-key='description'
                    >
                        <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={row.description ?? ''}>
                            {row.description}
                        </DefaultTooltipHostV9>
                    </div>
                    {shouldShowJournalEntry ? (
                        <>
                            <div
                                role='gridcell'
                                className={mergeClasses(styles.alignContentCenter, styles.lastModifiedByColumn, styles.fontSize)}
                            ></div>
                            <div
                                role='gridcell'
                                className={mergeClasses(
                                    styles.alignContentCenter,
                                    styles.journalEntryButtonHeader,
                                    styles.disabled,
                                    styles.fontSize
                                )}
                            >
                                <JournalEntryDialogButton
                                    className={drillDownStyles.iconOnlyButton}
                                    disabled={props.journalEntryButtonDisplayState === 'disabled'}
                                    onButtonClick={props.onJournalEntryClick!}
                                />
                            </div>
                            <div
                                role='gridcell'
                                className={mergeClasses(
                                    styles.alignContentCenter,
                                    styles.enterAmountsButtonHeader,
                                    styles.disabled,
                                    styles.fontSize
                                )}
                            ></div>
                        </>
                    ) : null}
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            styles.reportAmountColumn,
                            styles.alignContentCenter,
                            styles.fontWeightBold,
                            styles.fontSize,
                            shouldShowJournalEntry ? styles.disabled : undefined
                        )}
                        data-automation-key='amount'
                    >
                        {reformatNumericText(row.amount!, formatNumberForDisplay)}
                    </div>
                </TreeItemLayout>
            </TreeItem>

            {row.bookEntries
                ? row.bookEntries!.map((bookEntry: AccountAmountWhitePaperRow, index: number) => {
                      const treeLineStyle: string = index === 0 ? treeLineStyles.firstChild : treeLineStyles.subsequentChild;
                      const isLastEntry = index === row.bookEntries!.length - 1 && !row.parent?.adjustmentSummaries?.length;
                      const entryClassName = isLastEntry ? styles.treeRowLast : styles.treeRow;
                      return (
                          <AccountAmountBookEntryRow
                              row={bookEntry}
                              containerClassName={treeLineStyle}
                              className={entryClassName}
                              journalEntryButtonDisplayState={props.journalEntryButtonDisplayState}
                              key={bookEntry.uiKey!}
                              onJournalEntryClick={props.onJournalEntryClick}
                          />
                      );
                  })
                : null}
        </Tree>
    );
};
